import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Alert, Pagination } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import { obterOcorrenciasDoAtivo } from '~/Services/Service';
import FiltroData from './FiltroData';
import DBMemoTexto from './DBMemoTexto';
import useTitulosOcorrencia from '../hooks/useTitulosOcorrencia';
import useAtivosPorTitulo from '../hooks/useAtivosPorTitulo';
import useDatasPorAtivoTitulo from '../hooks/useDatasPorAtivoTitulo';

// W-0489 / T13 — Tab "Por Titulo": cascata 4 niveis (titulos -> ativos -> datas -> texto).
// Paridade Delphi UConsultaOcoAtivo.pas linhas 284-289 (lista titulos),
// 313-326 (cdOcorrenciaAfterScroll - ativos por titulo),
// 331-345 (cdAtivoAfterScroll - datas), 420-437 (texto).

const TAMANHO_PAGINA = 15;
const LABEL_SEM_TITULO = '(sem título)';

const formatarDataHora = (iso) =>
  iso ? moment(iso).format('DD/MM/YYYY HH:mm:ss') : '';

const exibirTitulo = (t) => (t ? t : LABEL_SEM_TITULO);

const extrairMsg = (err, fallback) =>
  (err && err.message) ? err.message : fallback;

// Paginador client-side reutilizavel (3 grids da cascata + GAP-A2-FE).
// Memoizado: re-renderiza so quando pagina/totalPaginas/total/inicio mudam.
const Paginador = React.memo(({ pagina, totalPaginas, total, inicio, onMudar }) => {
  if (totalPaginas <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center px-2 py-2 small">
      <span className="text-muted">
        {inicio + 1}–{Math.min(inicio + TAMANHO_PAGINA, total)} de {total}
      </span>
      <Pagination className="mb-0">
        <Pagination.Prev
          onClick={() => onMudar(Math.max(1, pagina - 1))}
          disabled={pagina === 1}
        />
        <Pagination.Item active>{pagina}</Pagination.Item>
        <Pagination.Next
          onClick={() => onMudar(Math.min(totalPaginas, pagina + 1))}
          disabled={pagina === totalPaginas}
        />
      </Pagination>
    </div>
  );
});

const TabPorTitulo = () => {
  const [dataInicio, setDataInicio] = useState(null);
  const [tituloSelecionado, setTituloSelecionado] = useState(null);
  const [ativoSelecionado, setAtivoSelecionado] = useState(null);
  const [dataSelecionada, setDataSelecionada] = useState(null);
  const [textoOcorrencia, setTextoOcorrencia] = useState('');
  const [carregandoTexto, setCarregandoTexto] = useState(false);
  const [paginaTitulos, setPaginaTitulos] = useState(1);
  const [paginaAtivos, setPaginaAtivos] = useState(1);
  const [paginaDatas, setPaginaDatas] = useState(1);

  const titulos = useTitulosOcorrencia(dataInicio);
  const ativos = useAtivosPorTitulo(
    tituloSelecionado != null ? tituloSelecionado.titulo : null,
    dataInicio
  );
  const datas = useDatasPorAtivoTitulo(
    tituloSelecionado != null ? tituloSelecionado.titulo : null,
    ativoSelecionado != null ? ativoSelecionado.codigo_ativo : null,
    dataInicio
  );

  // Resetar selecao em cascata quando ascendente muda (paridade Delphi
  // BitBtn5Click / cdAfterScroll que limpam os filhos). Tambem reseta paginas.
  useEffect(() => {
    setTituloSelecionado(null);
    setAtivoSelecionado(null);
    setDataSelecionada(null);
    setTextoOcorrencia('');
    setPaginaTitulos(1);
  }, [dataInicio]);

  useEffect(() => {
    setAtivoSelecionado(null);
    setDataSelecionada(null);
    setTextoOcorrencia('');
    setPaginaAtivos(1);
  }, [tituloSelecionado]);

  useEffect(() => {
    setDataSelecionada(null);
    setTextoOcorrencia('');
    setPaginaDatas(1);
  }, [ativoSelecionado]);

  // Buscar texto da ocorrencia quando data selecionada (paridade Delphi
  // UConsultaOcoAtivo.pas:420-437 — texto exibido ao escolher a data exata).
  useEffect(() => {
    if (
      tituloSelecionado == null ||
      ativoSelecionado == null ||
      dataSelecionada == null
    ) {
      setTextoOcorrencia('');
      return undefined;
    }
    let ativo = true;
    setCarregandoTexto(true);
    // Range .000-.999 (RN-7) — backend recebe dataInicio=data e dataFim=data+999ms.
    const dataExata = new Date(dataSelecionada.data);
    const dataFim = new Date(dataExata.getTime() + 999);
    obterOcorrenciasDoAtivo(ativoSelecionado.codigo_ativo, {
      titulo: tituloSelecionado.titulo,
      dataInicio: dataExata,
      dataFim,
    })
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const primeiro = lista.length > 0 ? lista[0] : null;
        setTextoOcorrencia(primeiro ? primeiro.texto || '' : '');
        setCarregandoTexto(false);
      })
      .catch((err) => {
        if (!ativo) return;
        setTextoOcorrencia('');
        setCarregandoTexto(false);
        toast.error(extrairMsg(err, 'Erro ao carregar texto da ocorrência.'));
      });
    return () => {
      ativo = false;
    };
  }, [tituloSelecionado, ativoSelecionado, dataSelecionada]);

  const colunasTitulos = useMemo(() => {
    const base = [
      { title: 'Título', key: '_tituloFmt', ordenacao: true, align: 'left' },
    ];
    // RN-4: quando filtro dataInicio preenchido, exibe coluna Data adicional.
    if (dataInicio) {
      base.unshift({
        title: 'Data',
        key: '_dataFmt',
        ordenacao: true,
        align: 'left',
        width: '180px',
      });
    }
    return base;
  }, [dataInicio]);

  const colunasAtivos = useMemo(
    () => [
      {
        title: 'Código',
        key: 'codigo_ativo',
        ordenacao: true,
        align: 'right',
        width: '120px',
      },
      { title: 'Nome', key: 'nome', ordenacao: true, align: 'left' },
    ],
    []
  );

  const colunasDatas = useMemo(
    () => [
      { title: 'Data/Hora', key: '_dataFmt', ordenacao: true, align: 'left' },
    ],
    []
  );

  const titulosFormatados = useMemo(
    () =>
      (titulos.items || []).map((t) => ({
        ...t,
        // Titulo NULL no banco vira '' na agregacao Q1/Q2 (R4 Delphi).
        // Exibe como "(sem titulo)" pra deixar claro pro usuario.
        _tituloFmt: exibirTitulo(t.titulo),
        _dataFmt: t.data_ocorrencia ? formatarDataHora(t.data_ocorrencia) : '',
      })),
    [titulos.items]
  );

  const datasFormatadas = useMemo(
    () =>
      (datas.items || []).map((d) => ({
        ...d,
        _dataFmt: formatarDataHora(d.data),
      })),
    [datas.items]
  );

  // Paginacao client-side (GAP-A2-FE / Gate 3 Q-03 D).
  const totalPagTitulos = Math.max(1, Math.ceil(titulosFormatados.length / TAMANHO_PAGINA));
  const totalPagAtivos = Math.max(1, Math.ceil((ativos.items || []).length / TAMANHO_PAGINA));
  const totalPagDatas = Math.max(1, Math.ceil(datasFormatadas.length / TAMANHO_PAGINA));
  const pTitulos = Math.min(paginaTitulos, totalPagTitulos);
  const pAtivos = Math.min(paginaAtivos, totalPagAtivos);
  const pDatas = Math.min(paginaDatas, totalPagDatas);
  const iniTitulos = (pTitulos - 1) * TAMANHO_PAGINA;
  const iniAtivos = (pAtivos - 1) * TAMANHO_PAGINA;
  const iniDatas = (pDatas - 1) * TAMANHO_PAGINA;
  const titulosPagina = titulosFormatados.slice(iniTitulos, iniTitulos + TAMANHO_PAGINA);
  const ativosPagina = (ativos.items || []).slice(iniAtivos, iniAtivos + TAMANHO_PAGINA);
  const datasPagina = datasFormatadas.slice(iniDatas, iniDatas + TAMANHO_PAGINA);

  return (
    <div className="p-2 p-md-3">
      <Row className="mb-3">
        <Col xs={12} sm={6} md={4} lg={3}>
          <FiltroData value={dataInicio} onChange={setDataInicio} />
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={4} className="mb-3">
          <Card>
            <Card.Header className="py-2">
              <strong>Títulos</strong>
              {titulos.loading && (
                <span className="ml-2 small text-muted">carregando…</span>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 360, overflow: 'auto' }}>
              {titulos.erro ? (
                <Alert variant="danger" className="m-2 mb-0">
                  {titulos.erro}
                </Alert>
              ) : titulos.loading ? (
                <Loading />
              ) : (
                <>
                  <TabelaDinamica
                    colunas={colunasTitulos}
                    dados={titulosPagina}
                    striped
                    hover
                    cliqueLinha={(item) => setTituloSelecionado(item)}
                    linhaSelecionada={(item) =>
                      tituloSelecionado != null &&
                      item.titulo === tituloSelecionado.titulo &&
                      (item.data_ocorrencia || null) ===
                        (tituloSelecionado.data_ocorrencia || null)
                    }
                  />
                  <Paginador
                    pagina={pTitulos}
                    totalPaginas={totalPagTitulos}
                    total={titulosFormatados.length}
                    inicio={iniTitulos}
                    onMudar={setPaginaTitulos}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4} className="mb-3">
          <Card>
            <Card.Header className="py-2">
              <strong>Ativos do Título</strong>
              {ativos.loading && (
                <span className="ml-2 small text-muted">carregando…</span>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 360, overflow: 'auto' }}>
              {tituloSelecionado == null ? (
                <div className="text-muted p-3 small">
                  Selecione um título para ver os ativos.
                </div>
              ) : ativos.erro ? (
                <Alert variant="danger" className="m-2 mb-0">
                  {ativos.erro}
                </Alert>
              ) : ativos.loading ? (
                <Loading />
              ) : (
                <>
                  <TabelaDinamica
                    colunas={colunasAtivos}
                    dados={ativosPagina}
                    striped
                    hover
                    cliqueLinha={(item) => setAtivoSelecionado(item)}
                    linhaSelecionada={(item) =>
                      ativoSelecionado != null &&
                      item.codigo_ativo === ativoSelecionado.codigo_ativo
                    }
                  />
                  <Paginador
                    pagina={pAtivos}
                    totalPaginas={totalPagAtivos}
                    total={(ativos.items || []).length}
                    inicio={iniAtivos}
                    onMudar={setPaginaAtivos}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4} className="mb-3">
          <Card>
            <Card.Header className="py-2">
              <strong>Datas da Ocorrência</strong>
              {datas.loading && (
                <span className="ml-2 small text-muted">carregando…</span>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 360, overflow: 'auto' }}>
              {ativoSelecionado == null ? (
                <div className="text-muted p-3 small">
                  Selecione um ativo para ver as datas.
                </div>
              ) : datas.erro ? (
                <Alert variant="danger" className="m-2 mb-0">
                  {datas.erro}
                </Alert>
              ) : datas.loading ? (
                <Loading />
              ) : (
                <>
                  <TabelaDinamica
                    colunas={colunasDatas}
                    dados={datasPagina}
                    striped
                    hover
                    cliqueLinha={(item) => setDataSelecionada(item)}
                    linhaSelecionada={(item) =>
                      dataSelecionada != null && item.data === dataSelecionada.data
                    }
                  />
                  <Paginador
                    pagina={pDatas}
                    totalPaginas={totalPagDatas}
                    total={datasFormatadas.length}
                    inicio={iniDatas}
                    onMudar={setPaginaDatas}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <Card.Header className="py-2">
              <strong>Texto da Ocorrência</strong>
            </Card.Header>
            <Card.Body>
              {carregandoTexto ? (
                <Loading />
              ) : (
                <DBMemoTexto
                  texto={textoOcorrencia}
                  placeholder="Selecione título, ativo e data para visualizar o texto."
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TabPorTitulo;
